import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { Play, Square, RefreshCw, Power, Copy } from "lucide-react"
import type { Server, ServerAction, ServerStatusReply } from "@mc/types"
import { getServer, getServerStatus, runServerAction } from "../api/servers.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { StatusDot } from "../components/ui/StatusDot.js"
import { useToast } from "../components/ui/Toast.js"

type PageState = { status: "loading" } | { status: "ready"; server: Server } | { status: "error"; message: string }

type StatusState = { status: "loading" } | { status: "ready"; data: ServerStatusReply } | { status: "error" }

/**
 * FR seccion 8/19 item 5 (overview del servidor). El widget de estado de
 * FR seccion 15 (estado, jugadores online/max, TPS, RAM, uptime) consume
 * GET /api/v1/servers/:serverId/status (services/gateway/src/routes/
 * servers.ts), cableado una vez que servers.queries.status tuvo un
 * responder real en server-manager (services/server-manager/src/nats/
 * queryHandlers.ts). Jugadores/TPS/RAM/uptime pueden seguir en null: ese
 * endpoint de Crafty especifico sigue sin verificar contra el spec real
 * (ver queryHandlers.ts), asi que se muestran solo si vienen con dato.
 */
export function ServerOverviewPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [statusState, setStatusState] = useState<StatusState>({ status: "loading" })
  const [pendingAction, setPendingAction] = useState<ServerAction | null>(null)
  const toast = useToast()

  useEffect(() => {
    if (!serverId) return
    let cancelled = false
    setState({ status: "loading" })
    getServer(serverId)
      .then(({ server }) => {
        if (!cancelled) setState({ status: "ready", server })
      })
      .catch((err) => {
        if (!cancelled) setState({ status: "error", message: friendlyError(err).message })
      })
    return () => {
      cancelled = true
    }
  }, [serverId])

  useEffect(() => {
    if (!serverId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    // Poll again shortly while the server is in a transitional state
    // (starting/stopping/restarting): Crafty's own start/stop/restart
    // calls are asynchronous (confirmed against its OpenAPI spec, see
    // CraftyClient.ts/queryHandlers.ts docs), so a single status read
    // right after `runAction()` will very often land mid-transition. A
    // fixed poll here is what turns "Comando enviado" into an actual
    // visible state change without the user having to refresh manually --
    // otherwise the UI would show a stale "starting" indefinitely even
    // once the server actually finished booting. Stops polling once the
    // state settles into a steady one (online/offline/crashed/unknown),
    // so this never polls forever for a server that just sits idle.
    const POLL_WHILE_TRANSITIONAL_MS = 4000
    const TRANSITIONAL_STATES: ServerStatusReply["state"][] = ["starting", "stopping", "restarting"]

    const poll = () => {
      setStatusState((prev) => (prev.status === "ready" ? prev : { status: "loading" }))
      getServerStatus(serverId)
        .then(({ status }) => {
          if (cancelled) return
          setStatusState({ status: "ready", data: status })
          if (TRANSITIONAL_STATES.includes(status.state)) {
            timer = setTimeout(poll, POLL_WHILE_TRANSITIONAL_MS)
          }
        })
        .catch(() => {
          // Non-fatal: identity (above) is the primary content of this page,
          // live status is a secondary widget. Fails quietly into its own
          // degraded card instead of blocking the whole page (Design principle
          // #1, "siempre mostrar el estado actual", still applies to failure).
          if (!cancelled) setStatusState({ status: "error" })
        })
    }

    poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [serverId, pendingAction])

  if (!serverId) return null

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {state.message}
      </p>
    )
  }

  const { server } = state

  const runAction = async (action: ServerAction) => {
    setPendingAction(action)
    try {
      await runServerAction(serverId, action)
      toast.show(`Comando enviado. El resultado va a reflejarse acá en unos segundos.`, "info")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setPendingAction(null)
    }
  }

  const runDestructive = (action: "stop" | "kill") => {
    const message =
      action === "stop"
        ? `Se va a detener "${server.name}".`
        : `Se va a forzar el apagado de "${server.name}". Esto puede provocar pérdida de progreso no guardado.`
    toast.confirmDestructive(
      message,
      () => runAction(action),
      () => toast.show("Acción cancelada.", "info"),
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>{server.name}</h1>
        <p style={{ color: "var(--text-muted)" }}>{server.description ?? "Sin descripción."}</p>
      </div>

      {server.publicIp && (
        <button
          onClick={async () => {
            await navigator.clipboard.writeText(server.publicIp as string)
            toast.show("IP copiada al portapapeles.", "success")
          }}
          style={{
            alignSelf: "flex-start",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            fontSize: "var(--text-sm)",
            color: "var(--text-primary)",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-2) var(--space-3)",
            transition: "border-color var(--duration-small) var(--ease-out)",
          }}
        >
          <Copy size={14} aria-hidden />
          {server.publicIp} · copiar IP
        </button>
      )}

      <div
        style={{
          padding: "var(--space-4)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-default)",
          background: "var(--bg-surface)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        {statusState.status === "loading" && (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <Spinner size="sm" />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>Consultando estado…</span>
          </div>
        )}
        {statusState.status === "error" && (
          <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
            No pudimos consultar el estado en vivo ahora mismo.
          </span>
        )}
        {statusState.status === "ready" && <ServerStatusWidget data={statusState.data} />}
      </div>

      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Button loading={pendingAction === "start"} onClick={() => runAction("start")}>
          <Play size={16} aria-hidden />
          Iniciar
        </Button>
        <Button variant="secondary" loading={pendingAction === "stop"} onClick={() => runDestructive("stop")}>
          <Square size={16} aria-hidden />
          Detener
        </Button>
        <Button variant="secondary" loading={pendingAction === "restart"} onClick={() => runAction("restart")}>
          <RefreshCw size={16} aria-hidden />
          Reiniciar
        </Button>
        <Button variant="destructive" loading={pendingAction === "kill"} onClick={() => runDestructive("kill")}>
          <Power size={16} aria-hidden />
          Forzar apagado
        </Button>
      </div>
    </div>
  )
}

function ServerStatusWidget({ data }: { data: ServerStatusReply }) {
  const metrics: { label: string; value: string }[] = []
  if (data.playersOnline !== null && data.playersMax !== null) {
    metrics.push({ label: "Jugadores", value: `${data.playersOnline} / ${data.playersMax}` })
  }
  if (data.tps !== null) metrics.push({ label: "TPS", value: data.tps.toFixed(1) })
  // ramAssignedMb sigue null (Crafty no lo reporta en GET .../stats, ver
  // CraftyServerStats): se muestra el usado solo cuando esta disponible,
  // en vez de exigir ambos valores como antes -- esa condicion original
  // nunca se cumplia una vez que ramUsedMb empezo a venir con datos reales
  // (queryHandlers.ts), porque ramAssignedMb nunca se puebla desde este
  // endpoint.
  if (data.ramUsedMb !== null) {
    metrics.push({
      label: "RAM",
      value: data.ramAssignedMb !== null ? `${data.ramUsedMb} / ${data.ramAssignedMb} MB` : `${data.ramUsedMb} MB`,
    })
  }
  if (data.uptimeSeconds !== null) metrics.push({ label: "Uptime", value: formatUptime(data.uptimeSeconds) })

  return (
    <>
      <StatusDot state={data.state} />
      {data.error === "crafty_down" && (
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          El panel de control (Crafty) no está respondiendo. Este estado puede no reflejar la realidad hasta que
          vuelva a responder.
        </span>
      )}
      {metrics.length > 0 && (
        <div style={{ display: "flex", gap: "var(--space-6)", flexWrap: "wrap" }}>
          {metrics.map((metric) => (
            <div key={metric.label} style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{metric.label}</span>
              <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)" }}>{metric.value}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function formatUptime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}
